import {
  blockedEntryEdge,
  edgeAxis,
  edgeIsMax,
  findBaseEntryConveyor,
  isInsideBase,
  stepBaseEdge,
} from './base';
import { isMethodInstallableIn } from '../content/market';
import type {
  SubscriberErrorMethodName,
  SubscriberMethodName,
  SubscriberNextMethodName,
} from '../content/market';
import type { ConveyorEntity } from './entities';
import { findEntityAt, inBounds } from './grid';
import { reachableFromObservables } from './routing';
import type { SimState } from './state';
import { oppositeDirection, samePos, translate } from './types';
import type { Direction, GridPos, GridRect } from './types';

export type SubscriberColumn = 'next' | 'error' | 'complete';
export type SubscriberFunctionKind = SubscriberMethodName;

/** Display order, left to right — independent of the order columns were revealed in. */
export const SUBSCRIBER_COLUMNS: readonly SubscriberColumn[] = ['next', 'error', 'complete'];

/**
 * Typed a column at a time rather than as one shared union, because each column's methods come from
 * its own service (see `SUBSCRIBER_SERVICES`) — which is what lets `stepSink` read `next` already
 * narrowed to something it can call, and `errorSubscription` read `error` the same way.
 */
export interface SubscriberFunctions {
  next: SubscriberNextMethodName[];
  error: SubscriberErrorMethodName[];
  complete: never[];
}

export interface Subscription {
  functions: SubscriberFunctions;
  /** Columns 'error' and 'complete' start hidden; 'next' is always present. */
  revealedColumns: SubscriberColumn[];
  /**
   * The single wall the subscription has committed to growing from. `null` at the 1×1 baseline (only
   * `'next'` revealed) — set on the first successful growth call, cleared again once collapse
   * returns the subscription to that same baseline.
   */
  committedDirection: Direction | null;
}

export function createSubscription(): Subscription {
  return {
    functions: { next: [], error: [], complete: [] },
    revealedColumns: ['next'],
    committedDirection: null,
  };
}

export type SubscriberSlotState = 'filled' | 'next-empty' | 'blank';

/** A column always shows exactly one clickable "+" immediately below its own last function. */
export function subscriberSlotState(
  box: Subscription,
  column: SubscriberColumn,
  row: number,
): SubscriberSlotState {
  const len = box.functions[column].length;
  if (row < len) return 'filled';
  if (row === len) return 'next-empty';
  return 'blank';
}

export interface SubscriberLayout {
  /** 'x' for east/west commitment (or uncommitted, at the 1×1 baseline); 'y' for north/south. */
  revealAxis: 'x' | 'y';
  /** Coordinate of reveal-index 0 — always the subscription's original placement cell. */
  anchor: number;
  /** +1 if reveal index grows toward `rect.max`, -1 toward `rect.min`. */
  sign: 1 | -1;
  /** The axis orthogonal to `revealAxis` — always `base.min`-anchored, never mirrored. */
  capacityAxis: 'x' | 'y';
}

/**
 * Which axis columns are revealed along, and which way, for the subscription's current commitment — the one
 * place the mirroring/transposition math lives, shared by {@link subscriberSlotAt} (hit-testing) and
 * `world-renderer.ts`'s `drawSubscriberColumns` (drawing), so they can never disagree.
 */
export function subscriberLayout(box: Subscription, base: GridRect): SubscriberLayout {
  const direction = box.committedDirection;
  const revealAxis = direction ? edgeAxis(direction) : 'x';
  const isMax = direction ? edgeIsMax(direction) : true;
  return {
    revealAxis,
    anchor: isMax ? base.min[revealAxis] : base.max[revealAxis],
    sign: isMax ? 1 : -1,
    capacityAxis: revealAxis === 'x' ? 'y' : 'x',
  };
}

/** Maps a grid cell inside the base rect to the column/row it represents, or null outside it. */
export function subscriberSlotAt(
  box: Subscription,
  base: GridRect,
  pos: GridPos,
): { column: SubscriberColumn; row: number } | null {
  if (!isInsideBase(base, pos)) return null;
  const { revealAxis, anchor, sign, capacityAxis } = subscriberLayout(box, base);
  const revealCoord = revealAxis === 'x' ? pos.x : pos.y;
  const capacityCoord = capacityAxis === 'x' ? pos.x : pos.y;
  const columnIndex = (revealCoord - anchor) * sign;
  const displayed = SUBSCRIBER_COLUMNS.filter((c) => box.revealedColumns.includes(c));
  const column = displayed[columnIndex];
  if (!column) return null;
  const row = capacityCoord - base.min[capacityAxis];
  return row < 0 ? null : { column, row };
}

export interface SubscriberSlotInfo {
  column: SubscriberColumn;
  row: number;
  state: SubscriberSlotState;
}

/** Combines {@link subscriberSlotAt} and {@link subscriberSlotState} for a single click site. */
export function subscriberSlotInfo(
  box: Subscription,
  base: GridRect,
  pos: GridPos,
): SubscriberSlotInfo | null {
  const at = subscriberSlotAt(box, base, pos);
  return at ? { ...at, state: subscriberSlotState(box, at.column, at.row) } : null;
}

/** The next column revealing would add, in fixed left-to-right order — null once all 3 are shown. */
export function nextRevealableColumn(box: Subscription): SubscriberColumn | null {
  return SUBSCRIBER_COLUMNS.find((c) => !box.revealedColumns.includes(c)) ?? null;
}

/** True once every column is empty — the subscription has never had a function placed in it. */
export function isSubscriptionEmpty(box: Subscription): boolean {
  return SUBSCRIBER_COLUMNS.every((column) => box.functions[column].length === 0);
}

export interface SubscriberDestroyAnchor {
  /** The grid cell to anchor the destroy control at. */
  pos: GridPos;
  /** Which pixel edge of that cell's rect to hang the control off, per axis. */
  xSide: 'min' | 'max';
  ySide: 'min' | 'max';
}

/**
 * Where to anchor the destroy control — the subscription's own corner farthest from the pipe entry, so it's
 * never the corner closest to the inbound connection. Falls back to an east-style default (same as
 * {@link subscriberLayout}) if the entry conveyor was erased out from under an already-placed base.
 * Deterministic tie-break along the axis orthogonal to the entry wall: always the `min` end of that
 * axis, so a 1×1 subscription always resolves to a single, stable corner. Returns explicit `min`/`max` sides
 * rather than just a cell, since a thin subscription makes a cell's coordinate alone ambiguous — for a 1-cell
 * axis, `pos` alone can't tell a caller which of that cell's two edges was actually intended.
 */
export function subscriberDestroyCorner(state: SimState): SubscriberDestroyAnchor | null {
  const base = state.base;
  if (!base) return null;
  const entryEdge = blockedEntryEdge(state);
  const farEdge = entryEdge ? oppositeDirection(entryEdge) : 'east';
  const axis = edgeAxis(farEdge);
  const otherAxis = axis === 'x' ? 'y' : 'x';
  const farSide: 'min' | 'max' = edgeIsMax(farEdge) ? 'max' : 'min';
  const farCoord = base[farSide][axis];
  const otherCoord = base.min[otherAxis];
  const pos = axis === 'x' ? { x: farCoord, y: otherCoord } : { x: otherCoord, y: farCoord };
  return {
    pos,
    xSide: axis === 'x' ? farSide : 'min',
    ySide: axis === 'y' ? farSide : 'min',
  };
}

/**
 * The conveyor with an open end, pre-placement — the "pipe terminal" that renders as a plug.
 * Prefers a candidate still reachable from an observable, so a stale stub left behind by a redraw
 * (its old direction rewritten mid-chain, orphaning whatever used to sit downstream) never wins
 * over the pipe's real, currently-live terminal. Falls back to the first open-ended conveyor when
 * no candidate is observable-reachable — e.g. a pipe left behind after its observable was erased — since
 * that's still a legitimate target for the "connect the Subscriber" CTA.
 */
export function danglingConveyor(state: SimState): ConveyorEntity | null {
  if (state.base) return null;
  const reachable = reachableFromObservables(state);
  let fallback: ConveyorEntity | null = null;
  for (const conveyor of Object.values(state.conveyors)) {
    const target = translate(conveyor.position, conveyor.direction);
    if (!inBounds(state, target) || findEntityAt(state, target)) continue;
    if (fallback === null) fallback = conveyor;
    if (reachable.has(conveyor.id)) return conveyor;
  }
  return fallback;
}

/**
 * The empty cell a dangling pipe end points into, pre-placement — where the "set up subscription"
 * CTA renders and where the Subscriber gets placed when it's clicked. Null once placed.
 */
export function danglingConveyorTarget(state: SimState): GridPos | null {
  const conveyor = danglingConveyor(state);
  return conveyor ? translate(conveyor.position, conveyor.direction) : null;
}

/**
 * Everywhere a click should be routed to the Subscriber instead of the normal build/paint flow:
 * the placed subscription itself, and whichever of the two subscription CTAs currently applies — "set up
 * subscription" pre-placement, or "plug in"/"pull plug" (the base entry conveyor) once it exists.
 * The subscription's own walls are a separate, pixel-space drag gesture handled outside this region check
 * (see pixi-app.ts's wall hit-test), the same way it was before columns existed.
 */
export function isSubscriberRegion(state: SimState, pos: GridPos): boolean {
  if (isInsideBase(state.base, pos)) return true;
  if (state.base === null) {
    const target = danglingConveyorTarget(state);
    return target !== null && samePos(target, pos);
  }
  const entry = findBaseEntryConveyor(state);
  return entry !== undefined && samePos(entry.position, pos);
}

/**
 * Whether `edge` is currently allowed to grow: never while the subscription is live, never the wall
 * the pipe enters through, and never anything but the subscription's already-committed direction once
 * one exists. Shared by `growSubscription` and the cursor-feedback path, so there's exactly one place
 * that answers "can this wall move."
 */
export function isEdgeGrowable(state: SimState, edge: Direction): boolean {
  const box = state.subscriber;
  if (!box || !state.base) return false;
  if (!canEditSubscription(state)) return false;
  if (blockedEntryEdge(state) === edge) return false;
  if (box.committedDirection !== null && box.committedDirection !== edge) return false;
  return true;
}

/**
 * Moves one wall of the Subscription by one cell, in either direction: growing it (revealing the
 * next hidden column, in fixed left-to-right order) or collapsing it (retracting the most recently
 * revealed column, only if empty). The first successful growth commits the subscription to that one edge —
 * every later call is restricted to it (see {@link isEdgeGrowable}) until a full collapse frees it
 * again. Reuses `stepBaseEdge`'s existing bounds/obstacle/sink checks for the actual rect move.
 */
export function growSubscription(state: SimState, edge: Direction, direction: 1 | -1): boolean {
  const box = state.subscriber;
  if (!box || !state.base) return false;
  if (!canEditSubscription(state)) return false;
  const expanding = edgeIsMax(edge) ? direction > 0 : direction < 0;
  return expanding
    ? growStep(state, box, edge, direction)
    : collapseStep(state, box, edge, direction);
}

function growStep(state: SimState, box: Subscription, edge: Direction, direction: 1 | -1): boolean {
  if (!isEdgeGrowable(state, edge)) return false;
  const column = nextRevealableColumn(box);
  if (column === null) return false;
  if (!stepBaseEdge(state, edge, direction)) return false;
  box.revealedColumns.push(column);
  if (box.committedDirection === null) box.committedDirection = edge;
  return true;
}

function collapseStep(
  state: SimState,
  box: Subscription,
  edge: Direction,
  direction: 1 | -1,
): boolean {
  if (box.committedDirection !== edge) return false;
  const outermost = box.revealedColumns[box.revealedColumns.length - 1];
  if (outermost === 'next') return false;
  if (box.functions[outermost].length > 0) return false;
  if (!stepBaseEdge(state, edge, direction)) return false;
  box.revealedColumns.pop();
  if (box.revealedColumns.length === 1) box.committedDirection = null;
  return true;
}

/**
 * A live subscription is frozen: its functions are the ones RxJS already handed to `.subscribe()`,
 * and swapping them mid-flight would model something RxJS cannot do. Tearing it down first is the
 * only way to change what the Subscriber is built from.
 */
export function canEditSubscription(state: SimState): boolean {
  return !state.subscribed;
}

/**
 * A slot the player is about to be refused: a real column/row of a subscription that is currently
 * live. Drives the cursor and the status-strip explanation, so the freeze announces itself on hover
 * rather than swallowing the click in silence.
 */
export function isSubscriberSlotFrozen(state: SimState, pos: GridPos): boolean {
  const box = state.subscriber;
  if (!box || !state.base || canEditSubscription(state)) return false;
  return subscriberSlotAt(box, state.base, pos) !== null;
}

/**
 * The widened view of a column, for the callers that only learn which column they are working on at
 * runtime. Writing through it is safe because {@link isMethodInstallableIn} has already refused
 * anything the column does not offer.
 */
function columnFunctions(box: Subscription, column: SubscriberColumn): SubscriberFunctionKind[] {
  return box.functions[column];
}

/**
 * Appends a function to `column`, within its current capacity along the axis orthogonal to the
 * subscription's reveal axis (see {@link subscriberLayout}) — e.g. subscription height for an east/west commitment,
 * subscription width for a north/south one. That axis never grows once a direction is committed (see
 * `growSubscription`), so every revealed column always shares the same available capacity: 1.
 */
export function addSubscriberFunction(
  state: SimState,
  column: SubscriberColumn,
  kind: SubscriberFunctionKind,
): boolean {
  const box = state.subscriber;
  const base = state.base;
  if (!box || !base || !canEditSubscription(state)) return false;
  if (!box.revealedColumns.includes(column)) return false;
  if (!isMethodInstallableIn(column, kind)) return false;
  const { capacityAxis } = subscriberLayout(box, base);
  const capacity = base.max[capacityAxis] - base.min[capacityAxis] + 1;
  const installed = columnFunctions(box, column);
  if (installed.length >= capacity) return false;
  installed.push(kind);
  return true;
}

/**
 * Swaps the function already installed at `row`, which is how a player recovers from installing one
 * that rejects everything their line produces. Only an occupied row qualifies — appending is
 * {@link addSubscriberFunction}'s job, and the two stay separate so a misaimed click can never
 * silently grow a column instead of editing it.
 */
export function replaceSubscriberFunction(
  state: SimState,
  column: SubscriberColumn,
  row: number,
  kind: SubscriberFunctionKind,
): boolean {
  const box = state.subscriber;
  if (!box || !canEditSubscription(state)) return false;
  if (!box.revealedColumns.includes(column)) return false;
  if (!isMethodInstallableIn(column, kind)) return false;
  const installed = columnFunctions(box, column);
  if (row < 0 || row >= installed.length) return false;
  installed[row] = kind;
  return true;
}
